/**
 * KPI target ranges hook - CR-6266
 *
 * Owns loading and CRUD for one KPI's time-based targets (OTIF, OTD or PPM).
 * Kept apart from `usePerformanceSettings`: a range saves on its own and never
 * round-trips the settings form. Where no range covers a date, the default
 * target in that form applies.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { performanceSettingsAPI, KPITargetKind, KPITargetRule, KPITargetRuleInput } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useConfirm, useAlertDialog } from '../../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';

/** A range being added or edited, as its inputs hold it ('' = empty). */
export interface KPITargetDraft {
  target_value: string;
  effective_from: string;
  effective_to: string;
}

const BLANK: KPITargetDraft = { target_value: '', effective_from: '', effective_to: '' };

/**
 * Manage one KPI's target ranges for the company being configured.
 *
 * @example
 * const ranges = useKpiTargetRules('otd', 'OTD');
 *
 * @param kpi - The KPI whose ranges to manage
 * @param label - The KPI's display name, for messages ("OTIF", "OTD", or the quality measure)
 * @returns The ranges, load state, the open editor's draft and the add / edit / delete handlers
 */
export function useKpiTargetRules(kpi: KPITargetKind, label: string) {
  const confirm = useConfirm();
  // CR-7271 pattern: a refused save is explained in a modal the user must
  // dismiss, not a toast that slides away mid-sentence.
  const alertDialog = useAlertDialog();

  const [rules, setRules] = useState<KPITargetRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The range being edited, 'new' while one is being added, else null. */
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<KPITargetDraft>(BLANK);
  const [saving, setSaving] = useState(false);
  // Read when a message is built, so a label that settles after mount (the
  // quality measure) does not refetch the list.
  const labelRef = useRef(label);
  labelRef.current = label;

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const response = await performanceSettingsAPI.getKPITargetRules(kpi);
      setRules(response.data.results);
    } catch (error: unknown) {
      console.error(`Failed to load ${labelRef.current} target ranges:`, error);
      setLoadError(getApiErrorMessage(error, `Failed to load ${labelRef.current} target ranges`));
    } finally {
      // Only the first load shows the loader; a refresh after a save keeps the table in place.
      setLoading(false);
    }
  }, [kpi]);

  useEffect(() => {
    void load();
  }, [load]);

  const startAdd = useCallback(() => {
    setEditing('new');
    setDraft(BLANK);
  }, []);

  const startEdit = useCallback((rule: KPITargetRule) => {
    setEditing(rule.id);
    setDraft({
      target_value: rule.target_value,
      effective_from: rule.effective_from,
      effective_to: rule.effective_to ?? '',
    });
  }, []);

  const cancel = useCallback(() => {
    setEditing(null);
    setDraft(BLANK);
  }, []);

  const changeDraft = useCallback((field: keyof KPITargetDraft, value: string) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  }, []);

  const save = useCallback(async () => {
    if (editing === null) return;
    if (!draft.target_value.trim() || !draft.effective_from) {
      toast.error('Enter a target and a start date');
      return;
    }
    const payload: KPITargetRuleInput = {
      kpi,
      target_value: draft.target_value.trim(),
      effective_from: draft.effective_from,
      effective_to: draft.effective_to || null,
    };
    try {
      setSaving(true);
      if (editing === 'new') {
        await performanceSettingsAPI.createKPITargetRule(payload);
      } else {
        await performanceSettingsAPI.updateKPITargetRule(editing, payload);
      }
      await load();
      setEditing(null);
      setDraft(BLANK);
      toast.success(`${label} target range saved`);
    } catch (error: unknown) {
      console.error(`Failed to save ${label} target range:`, error);
      await alertDialog({
        title: `${label} target range not saved`,
        description: getApiErrorMessage(error, `Failed to save ${label} target range`),
      });
    } finally {
      setSaving(false);
    }
  }, [editing, draft, kpi, label, load, alertDialog]);

  const remove = useCallback(async (rule: KPITargetRule, description: string) => {
    const ok = await confirm({
      title: `Delete ${label} Target Range`,
      description: `Delete the ${label} target ${description}? Its dates fall back to the default target.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await performanceSettingsAPI.deleteKPITargetRule(rule.id);
      await load();
      toast.success(`${label} target range deleted`);
    } catch (error: unknown) {
      console.error(`Failed to delete ${label} target range:`, error);
      await alertDialog({
        title: `${label} target range not deleted`,
        description: getApiErrorMessage(error, `Failed to delete ${label} target range`),
      });
    }
  }, [confirm, alertDialog, label, load]);

  return { rules, loading, loadError, reload: load, editing, draft, saving, startAdd, startEdit, cancel, changeDraft, save, remove };
}
